import React, { useState } from 'react';
import axios from 'axios';
import { Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge, ButtonGroup } from 'react-bootstrap';
import { FaCalendarCheck, FaCalendarTimes, FaUserClock, FaChartPie } from 'react-icons/fa';

const AppointmentReports = () => {
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');

  useState(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get('/api/doctors.php?active=1');
        setDoctors(res.data);
      } catch (err) {
        console.error('Error fetching doctors:', err);
      }
    };
    fetchDoctors();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { startDate, endDate };
      if (selectedDoctor) {
        params.doctorId = selectedDoctor;
      }
      const res = await axios.get('/api/appointment-reports.php', { params });
      setReportData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const quickDateFilter = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: <Badge bg="primary">Scheduled</Badge>,
      confirmed: <Badge bg="success">Confirmed</Badge>,
      completed: <Badge bg="info">Completed</Badge>,
      cancelled: <Badge bg="danger">Cancelled</Badge>,
      'no-show': <Badge bg="secondary">No Show</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Appointment Reports</h2>
        <ButtonGroup>
          <Button variant="outline-primary" size="sm" onClick={() => quickDateFilter(0)}>Today</Button>
          <Button variant="outline-primary" size="sm" onClick={() => quickDateFilter(7)}>Last 7 Days</Button>
          <Button variant="outline-primary" size="sm" onClick={() => quickDateFilter(30)}>Last 30 Days</Button>
        </ButtonGroup>
      </div>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">Select Doctor</Form.Label>
                <Form.Select
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  className="form-select-lg"
                >
                  <option value="">All Doctors</option>
                  {doctors.map(doc => (
                    <option key={doc.id} value={doc.id}>
                      Dr. {doc.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control-lg"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-bold">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control-lg"
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="primary" size="lg" onClick={fetchReport} className="w-100">
                <FaChartPie className="me-2" /> Generate
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading report...</p>
        </div>
      )}

      {reportData && !loading && (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #007bff'}}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">TOTAL APPOINTMENTS</p>
                      <h3 className="mb-0 text-primary fw-bold">{reportData.totalAppointments}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <FaCalendarCheck size={30} className="text-primary" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #28a745'}}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">COMPLETED</p>
                      <h3 className="mb-0 text-success fw-bold">{reportData.completed}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <FaUserClock size={30} className="text-success" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #dc3545'}}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">CANCELLED</p>
                      <h3 className="mb-0 text-danger fw-bold">{reportData.cancelled}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      <FaCalendarTimes size={30} className="text-danger" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #6c757d'}}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">NO SHOW</p>
                      <h3 className="mb-0 text-secondary fw-bold">{reportData.noShow}</h3>
                    </div>
                    <div className="bg-secondary bg-opacity-10 p-3 rounded">
                      <FaCalendarTimes size={30} className="text-secondary" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Doctor-wise Summary */}
          {reportData.doctorWise && reportData.doctorWise.length > 0 && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0 fw-bold">Doctor-wise Summary</h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 py-3">Doctor</th>
                      <th className="border-0 py-3 text-center">Total</th>
                      <th className="border-0 py-3 text-center">Completed</th>
                      <th className="border-0 py-3 text-center">Cancelled</th>
                      <th className="border-0 py-3 text-center">No Show</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.doctorWise.map((doc, index) => (
                      <tr key={index}>
                        <td className="py-3 fw-semibold">Dr. {doc.doctorName}</td>
                        <td className="py-3 text-center">{doc.total}</td>
                        <td className="py-3 text-center text-success fw-bold">{doc.completed}</td>
                        <td className="py-3 text-center text-danger">{doc.cancelled}</td>
                        <td className="py-3 text-center text-secondary">{doc.noShow}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Detailed Appointments */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0 fw-bold">Appointment Details</h5>
            </Card.Header>
            <Card.Body className="p-0">
              {reportData.details && reportData.details.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 py-3">Date</th>
                      <th className="border-0 py-3">Time</th>
                      <th className="border-0 py-3">Patient</th>
                      <th className="border-0 py-3">Doctor</th>
                      <th className="border-0 py-3">Type</th>
                      <th className="border-0 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.details.map((item, index) => (
                      <tr key={index}>
                        <td className="py-3">{new Date(item.date).toLocaleDateString('en-GB')}</td>
                        <td className="py-3">{item.time}</td>
                        <td className="py-3 fw-semibold">{item.patientName}</td>
                        <td className="py-3">Dr. {item.doctorName}</td>
                        <td className="py-3"><Badge bg="info">{item.type}</Badge></td>
                        <td className="py-3 text-center">{getStatusBadge(item.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted mb-0">📅 No appointments found for the selected period</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default AppointmentReports;
